// Сервис для работы с push-уведомлениями
import config from '../config/config';

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionRequest {
  endpoint: string;
  keys: PushSubscriptionKeys;
}

export interface PushSubscription {
  id: number;
  userId: number;
  endpoint: string;
  p256dh: string;
  auth: string;
  createdAt: string;
  updatedAt?: string;
  isActive: boolean;
}

class PushNotificationService {
  private vapidPublicKey: string | null = null;
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  constructor() {
    // VAPID ключ будет получен с сервера или из конфигурации
    // Для начала используем значение из appsettings.json
    this.init();
  }

  private async init() {
    try {
      // Получаем VAPID ключ с сервера (можно добавить эндпоинт /api/push/vapid-key)
      // Пока используем заглушку - в реальности нужно получать с сервера
      this.vapidPublicKey = null; // Будет получен с сервера
    } catch (error) {
      console.error('Error initializing push notification service:', error);
    }
  }

  /**
   * Запрашивает разрешение на уведомления
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('This browser does not support service workers');
      return 'denied';
    }

    if (!('PushManager' in window)) {
      console.warn('This browser does not support Push API');
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Проверяет текущий статус разрешения
   */
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Подписывается на push-уведомления
   */
  async subscribe(): Promise<PushSubscription | null> {
    try {
      // Запрашиваем разрешение
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission not granted');
        return null;
      }

      // Регистрируем Service Worker если еще не зарегистрирован
      if (!this.registration) {
        this.registration = await navigator.serviceWorker.ready;
      }

      // Получаем существующую подписку или создаем новую
      let pushSubscription = await this.registration.pushManager.getSubscription();

      if (!pushSubscription) {
        // Получаем VAPID ключ с сервера
        const vapidKey = await this.getVapidPublicKey();
        if (!vapidKey) {
          console.error('VAPID public key not available');
          return null;
        }

        // Создаем новую подписку
        pushSubscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidKey),
        });
      }

      // Отправляем подписку на сервер
      const subscriptionData = {
        endpoint: pushSubscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(pushSubscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(pushSubscription.getKey('auth')!),
        },
      };

      const savedSubscription = await this.saveSubscription(subscriptionData);
      this.subscription = savedSubscription;

      return savedSubscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  }

  /**
   * Отписывается от push-уведомлений
   */
  async unsubscribe(): Promise<boolean> {
    try {
      if (!this.registration) {
        this.registration = await navigator.serviceWorker.ready;
      }

      const pushSubscription = await this.registration.pushManager.getSubscription();
      if (pushSubscription) {
        // Удаляем подписку на сервере
        if (this.subscription?.endpoint) {
          await this.deleteSubscription(this.subscription.endpoint);
        }

        // Удаляем подписку в браузере
        await pushSubscription.unsubscribe();
        this.subscription = null;

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  /**
   * Проверяет, подписан ли пользователь
   */
  async isSubscribed(): Promise<boolean> {
    try {
      if (!this.registration) {
        this.registration = await navigator.serviceWorker.ready;
      }

      const pushSubscription = await this.registration.pushManager.getSubscription();
      return pushSubscription !== null;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Получает VAPID публичный ключ с сервера
   */
  private async getVapidPublicKey(): Promise<string | null> {
    try {
      // Временное решение - получаем из конфигурации
      // В будущем можно добавить эндпоинт /api/push/vapid-key
      const response = await fetch(`${config.api.baseURL}/pushvapidkey/vapid-key`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.publicKey || null;
      }

      // Fallback: используем значение из конфигурации (в реальности должно быть на сервере)
      return null;
    } catch (error) {
      console.error('Error getting VAPID public key:', error);
      return null;
    }
  }

  /**
   * Сохраняет подписку на сервере
   */
  private async saveSubscription(subscription: PushSubscriptionRequest): Promise<PushSubscription | null> {
    try {
      const response = await fetch(`${config.api.baseURL}/pushsubscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(subscription),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      throw new Error(`Failed to save subscription: ${response.statusText}`);
    } catch (error) {
      console.error('Error saving subscription:', error);
      return null;
    }
  }

  /**
   * Удаляет подписку на сервере
   */
  private async deleteSubscription(endpoint: string): Promise<boolean> {
    try {
      const encodedEndpoint = encodeURIComponent(endpoint);
      const response = await fetch(`${config.api.baseURL}/pushsubscription/endpoint/${encodedEndpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting subscription:', error);
      return false;
    }
  }

  /**
   * Преобразует base64 строку в Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Преобразует ArrayBuffer в base64 строку
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

// Создаем глобальный экземпляр
export const pushNotificationService = new PushNotificationService();

